'use client';
import { usePathname } from 'next/navigation';

import { getUrlOrigin } from '@monoimin/utils/helpers';

import type { PropsWithChildren, MouseEvent } from 'react';

type AnalyticsProviderProps = PropsWithChildren<{
  className?: string;
  as: 'div' | 'footer' | 'header' | 'li' | 'nav';
  categoryType: string;
  action: string;
  sendToRenta?: boolean;
  onClickAnchor?: () => void;
}>;

const checkIsAnchor = (target: EventTarget) => {
  return (
    'getAttribute' in target &&
    typeof target.getAttribute === 'function' &&
    Boolean(target.getAttribute('href'))
  );
};

const checkIsButton = (target: EventTarget) => {
  if ('tagName' in target) {
    return target.tagName === 'BUTTON';
  }

  return false;
};

const getAttributeName = (target: EventTarget) => {
  if ('innerText' in target && target.innerText) {
    return (target as HTMLAnchorElement).innerText;
  }

  if ('name' in target && target.name) {
    return target.name as string;
  }

  return '';
};

const getAttributeUrl = (target: EventTarget) => {
  const isAnchor = checkIsAnchor(target);

  if (isAnchor) {
    return (target as HTMLAnchorElement).getAttribute('href');
  }

  return '';
};

const defineActionType = (target: EventTarget) => {
  const isAnchor = checkIsAnchor(target);

  if (isAnchor) {
    return 'Клик по ссылке';
  }

  const isButton = checkIsButton(target);

  if (isButton) {
    return 'Click';
  }

  return '';
};

export const AnalyticsClickProvider = ({
  children,
  className,
  as,
  categoryType,
  action,
  sendToRenta,
  onClickAnchor,
}: AnalyticsProviderProps) => {
  const Root = as;

  const pathname = usePathname();

  const onClick = (event: MouseEvent<HTMLElement>) => {
    const target = event.target;

    event.stopPropagation();

    const isAnchor = checkIsAnchor(target);

    const isButton = checkIsButton(target);

    if (!(isAnchor || isButton)) {
      return;
    }

    const origin = getUrlOrigin();

    const data = {
      category_type: categoryType,
      action_type: defineActionType(target),
      attribute_name: getAttributeName(target),
      attribute_url: getAttributeUrl(target),
      title: document.title,
      domain: origin,
      url: `${origin}${pathname}`,
      uri: pathname,
    };

    if ('dataLayer' in window) {
      window.dataLayer.push({
        ...data,
        event: action,
      });
    }

    if (
      sendToRenta &&
      'renta' in window &&
      typeof window.renta?.track === 'function'
    ) {
      window.renta.track(action, data);
    }

    if (isAnchor && typeof onClickAnchor === 'function') {
      onClickAnchor();
    }
  };

  return (
    <Root className={className} onClick={onClick}>
      {children}
    </Root>
  );
};

export default AnalyticsClickProvider;
